import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { listInventoryByNdcApi } from 'api/inventory';

const toTitleCase = (name) => (name ? name.replace(/\b\w/g, (c) => c.toUpperCase()) : '');

// Debounced NDC / drug-name lookup. Reports every change as
// { ndc, drugName, isSelected, option } — isSelected is true only when the
// value was picked from the drug list, which callers must require
// before accepting the line; option is the picked inventory item, else null.
// getOptionDisabledReason, when given, greys out an option and shows why.
// includeInactive also searches deactivated NDCs, which are labelled as such.
const NdcAutocomplete = ({
  label,
  value,
  onChange,
  error,
  helperText,
  getOptionDisabledReason,
  includeInactive,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const handleSelect = (_, newVal) => {
    const isObject = typeof newVal === 'object' && newVal !== null;
    const baseName = isObject ? toTitleCase(newVal.drugName ?? '') : '';
    const strength = isObject ? (newVal.strength ?? '') : '';
    onChange({
      ndc: typeof newVal === 'string' ? newVal : (newVal?.ndc ?? ''),
      drugName: strength ? `${baseName} ${strength}` : baseName,
      isSelected: isObject,
      option: isObject ? newVal : null,
    });
  };

  const handleInputChange = (_, inputVal, reason) => {
    if (reason === 'input') {
      onChange({ ndc: inputVal, drugName: '', isSelected: false, option: null });
      clearTimeout(timerRef.current);
      if (inputVal.length >= 3) {
        setLoading(true);
        timerRef.current = setTimeout(() => {
          listInventoryByNdcApi(inputVal, { includeInactive })
            .then(setOptions)
            .finally(() => setLoading(false));
        }, 300);
      } else {
        setLoading(false);
        setOptions([]);
      }
    } else if (reason === 'clear') {
      onChange({ ndc: '', drugName: '', isSelected: false, option: null });
      setOptions([]);
    }
  };

  return (
    <Autocomplete
      size='small'
      fullWidth
      freeSolo
      loading={loading}
      options={options}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.ndc)}
      isOptionEqualToValue={(opt, val) => opt.ndc === (typeof val === 'string' ? val : val.ndc)}
      filterOptions={(x) => x}
      getOptionDisabled={(opt) => !!getOptionDisabledReason?.(opt)}
      value={value}
      onChange={handleSelect}
      onInputChange={handleInputChange}
      renderOption={(props, opt) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <li {...props} key={opt.ndc}>
          <Box>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              {opt.ndc}
            </Typography>
            <Typography variant='body2'>
              {toTitleCase(opt.drugName)}
              {opt.strength ? ` — ${opt.strength}` : ''}
            </Typography>
            {opt.manufacturer && (
              <Typography variant='caption' color='text.secondary'>
                Manufacturer: {opt.manufacturer}
              </Typography>
            )}
            {opt.isActive === false && (
              <Typography variant='caption' color='warning.main' sx={{ display: 'block' }}>
                Deactivated
              </Typography>
            )}
            {getOptionDisabledReason?.(opt) && (
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                {getOptionDisabledReason(opt)}
              </Typography>
            )}
          </Box>
        </li>
      )}
      componentsProps={{ paper: { sx: { minWidth: 360 } } }}
      renderInput={(params) => (
        <MuiTextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          FormHelperTextProps={{ sx: { ml: 0 } }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color='inherit' size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

NdcAutocomplete.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  getOptionDisabledReason: PropTypes.func,
  includeInactive: PropTypes.bool,
};

NdcAutocomplete.defaultProps = {
  label: 'NDC / Drug Name *',
  value: '',
  error: false,
  helperText: '',
  getOptionDisabledReason: null,
  includeInactive: false,
};

export default NdcAutocomplete;
